import React, {useEffect, useState} from 'react';
import MainLayout from "../../layouts/main";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {useGlobalNotificationMessage} from "../../context/message";
import {useEventService} from "../../services/event-service";
import InitialLoading from "../../components/initial-loading";
import {AppEvents, AppRoutes, AttendanceTypes, SearchTypes} from "../../utils/constants";
import StarRating from "../../components/star-rating";
import {useTrainerService} from "../../services/trainer-service";
import EventSchedule from "../../components/event-schedule";
import MapCell from "../../components/map-cell";
import {Modal, OverlayTrigger, Tooltip} from "react-bootstrap";
import {useNotifyEventsService} from "../../services/notify-events-service";
import HighlightLinks from "../../components/highlight-links";
import {useAuth} from "../../context/auth";
import {useFavouritesService} from "../../services/favourites-service";
import {ClickableSlider} from "../../components/clickable-slider";

export default function EventDetail () {
  const [loaded, setLoaded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [realAttendeesCount, setRealAttendeesCount] = useState(0);
  const [trainer, setTrainer] = useState(null);
  const [showMoreClasses, setShowMoreClasses] = useState(false);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);

  const {addErrorMessage} = useGlobalNotificationMessage();
  const eventService = useEventService();
  const trainerService = useTrainerService();
  const favouriteService = useFavouritesService();
  const nes = useNotifyEventsService();
  const {id} = useParams();
  const auth = useAuth();

  const [searchParams] = useSearchParams();
  const ESID = searchParams.get('esid') || null;

    const photosSliderSettings = {
        swipe: false,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true
    };

  useEffect(() => {
    if (id){
      reloadEventDetail();
      nes.on(AppEvents.attendanceChanged, reloadEventDetail);
    }
    // eslint-disable-next-line
  }, [id, ESID]);

  async function reloadEventDetail(){
    let promises = [
    ];
    if (ESID){
      //we try to load esdetail, but if we fail, we re-try with regular detail.
      let esdetail = null;
      try{
        esdetail = await eventService.getEsDetail(ESID);
      } catch (e) {
        promises.push(eventService.getDetail(id));
      }
      promises.push(esdetail);
    } else {
      promises.push(eventService.getDetail(id));
    }

    Promise.all(promises).then(async ([eventRes]) => {
      setDetail(eventRes || null);

      if (eventRes.trainer.id===auth.currentUser.id || eventRes?.events[0]?.attendance?.isCotrainer){
        const attendeesRes =  await eventService.getAttendeeList(id);
        setAttendees(attendeesRes || null);

        let rac = 0;
        if (attendeesRes && attendeesRes.length>0) {
          attendeesRes.forEach(a => {
            if (!a?.attendance?.isCancelled && !a?.attendance?.isCotrainer){
              rac++;
            }
          })
        }
        setRealAttendeesCount(rac);
      }
      if (!trainer){
        const trainerRes = await trainerService.getDetail(eventRes.trainer.id);
        setTrainer(trainerRes);
      }
      setLoaded(true);
    }).catch(error => {
      HelperService.handleApiErrors(error, addErrorMessage);
    });
  }

  const TrainerCell = function ({trainer, ...props}) {
    return (<Link to={`${AppRoutes.trainers.path}/${trainer.id}`} className="router-link text-dark">
      <div className="d-flex align-items-center gap-2">
        <div className="rounded-circle bg-cover"
             style={{backgroundImage: `url(${trainer.photo})`, width: '90px', height: '90px'}}></div>
        <div className="px-2">
          <h6>{trainer.name} {trainer.surname}</h6>
          <StarRating rating={trainer.rating} showCount={true} count={trainer.reviews} />
        </div>
      </div>
    </Link>);
  }

  const PricingCell = function({price, capacity=null}){
    let pm, pn = null;

    if (capacity.nonMember!==0 && price.nonMember!==null){
      pn = price.nonMember===0 ? 'FREE' : '$'+price.nonMember;
    }
    if (capacity.member!==0 && price.member!==null){
      pm = price.member===0 ? 'FREE' : '$'+price.member;
    }

    return <>{(pm || pn) && <div className="">
      {pm && <div className="mb-1">Prepaid / Member from: {pm}</div>}
      {pn && <div className="">Pay per event / Walk-in from: {pn}</div>}
    </div>}</>
  }

  const AttendeeCell = function({attendee}){
    const renderActions = () => (
        <Tooltip className="rounded-3 shadow-sm border-1 border-secondary opacity-100">
          <div className="list-group list-group-flush rounded-2 overflow-hidden my-1">
            {attendee?.mobile && <a href={`tel:${attendee.mobile}`} type="button" className="list-group-item list-group-item-action">Call {attendee.mobile}</a>}
            {attendee?.mobile && <a href={`sms:${attendee.mobile}`} type="button" className="list-group-item list-group-item-action">Contact via SMS</a>}
            {attendee?.email && <a href={`mailto:${attendee.email}`} type="button" className="list-group-item list-group-item-action">Contact via email</a>}
            {attendee?.trainer && <Link to={`${AppRoutes.trainers.path}/${attendee?.id}`}><span className="list-group-item list-group-item-action">Show FitPro profile</span></Link>}
          </div>
        </Tooltip>
    );
    const renderTooltip = (message) => (
        <Tooltip>{message}</Tooltip>
    );

    return <div className="card mb-2 me-2 d-inline-block border-0 rounded-3 hover-cell-effect-subtle">
      <div className="card-body" style={{minHeight: '111px'}}>
        <OverlayTrigger trigger="click" placement="bottom" delay={{ show: 50, hide: 100 }} overlay={renderActions()}>
          <div className="d-flex align-items-center gap-2 mb-2 clickable">
            <div style={{width:'46px', height:'46px'}}>
              <img className="img-fit rounded-circle" alt='' src={attendee.photo} />
            </div>
            <div title={attendee.name + ' ' + attendee.surname}>
                <div>{attendee.name}</div>
                <div style={{width:'80px', whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{attendee.surname}</div>
            </div>
          </div>
        </OverlayTrigger>
        <div className="d-flex align-items-center gap-2">
          {attendee?.attendance.type === AttendanceTypes.nonMember &&
              <OverlayTrigger placement="bottom" delay={{ show: 50, hide: 100 }} overlay={renderTooltip('Walk-in')}>
                <div className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center" style={{width: '25px', height:'25px', cursor:'default'}}>W</div>
              </OverlayTrigger>}
          {(attendee?.attendance.isCancelled===true || attendee?.attendance.isRefundRequested===true) &&
              <OverlayTrigger placement="bottom" delay={{ show: 50, hide: 100 }} overlay={renderTooltip('Cancelled')}>
              <div className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center text-danger" style={{width: '25px', height:'25px'}}><span className="mdi mdi-cancel"></span></div>
              </OverlayTrigger>}
          {attendee?.attendance.isPaid===true && !(attendee?.attendance.isCancelled===true || attendee?.attendance.isRefundRequested===true) &&
              <OverlayTrigger placement="bottom" delay={{ show: 50, hide: 100 }} overlay={renderTooltip('Paid')}>
              <div className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center text-success" style={{width: '25px', height:'25px'}}><span className="mdi mdi-credit-card-check-outline"></span></div>
              </OverlayTrigger>}
          {attendee?.trainer &&
              <OverlayTrigger placement="bottom" delay={{ show: 50, hide: 100 }} overlay={renderTooltip('Trainer')}>
              <div className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center" style={{width: '25px', height:'25px'}}><span className="mdi mdi-whistle-outline"></span></div>
              </OverlayTrigger>}

        </div>
        {/*{attendee?.attendance?.isCotrainer &&*/}
        {/*  <div className="d-flex align-items-center justify-content-start text-primary mt-2 fs-7">Co-instructing</div>}*/}
      </div>
    </div>
  }

  const ShowClassesModal = function({classes}) {
    const closeModal = () => setShowMoreClasses(false);

    return <Modal size='xl' show={!!showMoreClasses} onHide={closeModal} centered>
      <Modal.Header closeButton>
        <h4 className="header-stripe-blue col-sm-6 pb-1">{detail.course?.name}</h4>
      </Modal.Header>
      <Modal.Body className="p-5">
        <Section animation={HelperService.getRandomAnimation()}>
          <div className="row">
            <div className="col-sm-12">
              <div className="bg-light p-2 rounded-3">
                <EventSchedule events={classes} />
              </div>
            </div>
          </div>
        </Section>
      </Modal.Body>
    </Modal>
  };

  const toggleLikeTrainer = function (t){
    if (t.trainer.liked){
      favouriteService
          .unlikeTrainer(t.id)
          .then(() => {
            let newTrainer = Object.assign({}, t);
            newTrainer.trainer = Object.assign({}, t.trainer, {liked: false});
            setTrainer(newTrainer);
          })
          .catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
          });
    } else {
      favouriteService
          .likeTrainers([t.id])
          .then(() => {
            let newTrainer = Object.assign({}, t);
            newTrainer.trainer = Object.assign({}, t.trainer, {liked: true});
            setTrainer(newTrainer);
          })
          .catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
          });
    }
  }

  const CancellationPolicyModal = function () {
    const closeModal = () => setShowCancellationPolicy(false);

    return <Modal size='md' show={!!showCancellationPolicy} onHide={closeModal} centered>
      <Modal.Header closeButton>
        <h4 className="header-stripe-blue pb-1">Cancellation policy</h4>
      </Modal.Header>
      <Modal.Body className="p-5">
        <Section animation={HelperService.getRandomAnimation()}>
          <div className="row pb-3">
            <div className="p-3 mb-3" dangerouslySetInnerHTML={{__html: detail?.events[0]?.cancellationPolicy}}></div>
          </div>
        </Section>
      </Modal.Body>
      <Modal.Footer>
        <div className="col-sm-12 d-flex justify-content-center gap-2">
          <button className="btn btn-outline-primary" onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}>Close</button>
        </div>
      </Modal.Footer>
    </Modal>
  };

  return (
      <>

        {!loaded && <InitialLoading/>}

        {loaded && !detail && <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding-first">
        <div className={"container"}>
            <div className={"row"}>
              <div className={"col-sm-12"}>
                <h1 className="header-stripe-blue">Event detail</h1>
              <p>Info about event</p>
            </div>
            <div className={"col-sm-12 col-md-6"}>
              <h2>:( we were not able to find the event you were looking for.</h2>
              <p>Try searching for it from home screen</p>
            </div>
          </div>
        </div>
      </Section>}

      {loaded && detail && <>
        <Section animation={HelperService.getRandomAnimation()} className="position-relative section-padding-first">
          <div className={"container"}>
            <div className={"row"}>


              <div className={"col-sm-12 col-md-5 mb-4"}>
                <h1 className="header-stripe-blue">{detail.course?.name}</h1>
                {detail.course?.categories?.length > 0 && <>
                  <div className="mt-2">
                    {detail.course?.categories.map((c) => {
                      return <Link
                          className="router-link text-dark"
                          key={c.id}
                          to={`${AppRoutes.searchEvents.path}?keyword=${detail.trainer?.name + ' ' + detail.trainer?.surname}&type=${SearchTypes.discover}&categories=[${c.id}]`}>
                        <span className="me-1 mb-1">{c.name}</span>
                      </Link>
                    })}
                  </div>
                  <div className="mt-2">
                    <StarRating rating={detail.course?.rating} count={detail.course?.ratingCount} showCount={false}/>
                  </div>
                  <div className="mt-4">
                    <h5>Price</h5>
                    <PricingCell price={detail?.events[0]?.price} capacity={detail?.events[0]?.capacity}/>
                    {detail?.events[0]?.cancellationPolicy && detail?.events[0]?.price?.isPaymentRequired && <div className="mt-2">
                      <button className="link-dark clickable text-decoration-underline d-inline w-auto bg-transparent border-0 p-0"
                              onClick={() => setShowCancellationPolicy(!showCancellationPolicy)}>Event
                        cancellation policy
                      </button>
                    </div>}
                  </div>
                  {detail?.events[0]?.note && <div className="mt-5">
                    <h5>Note</h5>
                    <div className="p-3 bg-warning-subtle"><HighlightLinks text={detail.events[0]?.note}/></div>
                  </div>}
                  {detail?.events[0]?.info && <div className="mt-5">
                    <h5>Event & location details</h5>
                    <div className="py-2"><HighlightLinks text={detail.events[0]?.info}/></div>
                  </div>}

                    {detail.course?.gallery.filter(g=>g.type==="VIDEO").length>0 && <div className="mt-4">
                        <h5>Video</h5>
                        {detail.course?.gallery.filter(g=>g.type==="VIDEO").map(t => <React.Fragment key={t.id}>
                                {t.type === "VIDEO" &&
                                    <div className={'card rounded-3 overflow-hidden mb-3'} style={{width:"220px"}}>
                                        <video width="100%" poster={t.thumbnail} controls preload="metadata">
                                            <source src={t.url} type="video/mp4"/>
                                        </video>
                                    </div>
                                }
                            </React.Fragment>
                        )}
                    </div>}

                </>}
              </div>


              <div className={"col-sm-12 col-md-7 mb-4"}>
                  <div className="mb-5"><ClickableSlider {...photosSliderSettings}>
                      <div className="card border-0 overflow-hidden rounded-3">
                          <img alt={''} src={detail.course?.cover} className="img-fit" />
                      </div>
                      {detail.course?.gallery.filter(g=>g.type==="photo").map(t => <React.Fragment key={t.id}>
                              {t.type === "PHOTO" &&
                                  <div className={'card rounded-3 overflow-hidden mb-3'}>
                                      <img src={t.url} alt="" className="img-fit"/>
                                  </div>
                              }
                          </React.Fragment>
                      )}
                  </ClickableSlider></div>
              </div>

              <div className={"col-sm-12 col-md-7 mb-5"}>
                <h5><span
                    className="text-primary">{detail?.events[0]?.isRescheduled ? 'Resheduled to' : 'Event'}</span> {HelperService.getNiceDateString(detail?.events[0]?.datetime, detail?.events[0]?.timezone)} {HelperService.getEventOffsetString(detail?.events[0]?.datetime, detail?.events[0]?.timezone)}
                </h5>
                <div className="bg-light p-1 p-sm-3 rounded-3">
                  {detail?.events.length > 0 && <EventSchedule trainer={trainer} events={[detail?.events[0]]}/>}
                  {detail?.events.length > 1 && <div>
                    <button className="link-primary my-3 d-inline-block text-decoration-underline d-inline w-auto bg-transparent border-0 p-0" onClick={() => setShowMoreClasses(true)}>Show more
                      events
                    </button>
                  </div>}
                  <ShowClassesModal classes={detail?.events}/>
                </div>
              </div>

              {(detail?.isOwner || detail?.events[0]?.attendance?.isCotrainer) && <div className={"col-sm-12 mb-5"}>
                <h5>Attendees {realAttendeesCount>0 && <span title="Number of attendees, excluding cancelled users and cotrainers.">({realAttendeesCount})</span>}</h5>
                {attendees?.length === 0 && <>
                  <div className="text-muted">There are no attendees yet.</div>
                </>}
                {attendees?.length > 0 && <div className="d-flex flex-wrap">
                  {attendees.map((a) => <AttendeeCell key={a?.attendance.code} attendee={a}/>)}
                </div>}
              </div>}

              <div className="clearfix"></div>

              <div className={"col-sm-12 col-md-6 col-lg-4 mb-4"}>
                <h5>Description</h5>
                <p>{detail?.course?.info}</p>
              </div>
              <div className={"col-sm-12 col-md-6 col-lg-4 mb-4"}>
                <h5>{detail?.place?.name}</h5>
                <p>
                  {detail?.place?.location?.lat && detail?.place?.location?.lon &&
                      <Link to={`https://www.google.com/maps/search/?api=1&query=${detail?.place?.location?.lat},${detail?.place?.location?.lon}`}>{detail?.place?.location?.address}</Link>}
                  {(!detail?.place?.location?.lat || !detail?.place?.location?.lon) && <>{detail?.place?.location?.address}</>}
                </p>
                {detail?.place?.location?.lat && detail?.place?.location?.lon &&
                    <div className='rounded-3 overflow-hidden' style={{height: '250px'}}><MapCell lat={detail?.place?.location?.lat} lon={detail?.place?.location?.lon} controls={false} gps={false}/>
                    </div>}
              </div>

              <div className={"col-sm-12 col-md-6 col-lg-4 mb-4"}>
                <h5>About FitPro</h5>
                <div className="my-2 mb-4">
                  <TrainerCell trainer={detail?.trainer}/>
                </div>
                <div className="my-2 mb-4">
                  <Link to={`${AppRoutes.trainers.path}/${detail?.trainer?.id}`}>
                    <button className="btn btn-primary btn-sm me-2">View schedule / Contacts</button>
                  </Link>
                  {detail?.trainer?.id && detail?.trainer?.id !== auth.currentUser.id && <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleLikeTrainer(trainer);
                      }}
                      className={`btn btn-sm ${trainer.trainer.liked ? 'btn-outline-primary' : 'btn-primary'}`}>
                    {trainer.trainer.liked ? 'Following' : 'Follow'}
                  </button>}
                </div>
                {trainer?.trainer?.info && <div dangerouslySetInnerHTML={{__html: trainer?.trainer?.info}}></div>}
              </div>


            </div>
          </div>
        </Section>

        <CancellationPolicyModal/>
      </>}

      </>
  );
}

EventDetail.getLayout = function getLayout(page) {
  return (
      <MainLayout>
        {page}
      </MainLayout>
  )
}